import { Fragment, useState } from 'react'
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronDown, ChevronRight, Download, FileJson } from 'lucide-react'
import { api } from '@/api/client'
import type { RocRow, RocVersion, RocProblem } from '@/api/ma-tax-schemas'
import { useToast } from '@/hooks/use-toast'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { Spinner } from '@/components/ui/spinner'
import { ErrorState } from '@/components/ui/error-state'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { formatRate } from '@/lib/ma-tax-format'
import { OwnerEmailsPanel } from '@/components/tax-reports/owner-emails-panel'

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

const money = (value: number) =>
  value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** The month before today: the return that is due now. */
function defaultPeriod(): { year: number; month: number } {
  const now = new Date()
  const month = now.getMonth() === 0 ? 12 : now.getMonth()
  return { year: month === 12 ? now.getFullYear() - 1 : now.getFullYear(), month }
}

function periodLabel(period: string) {
  const [y, m] = period.split('-').map(Number)
  return `${MONTHS[m - 1]} ${y}`
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/** "2026-08-02" -> "Aug 2, 2026", read as a calendar date (no time zone shift). */
function shortDate(isoDate: string | null) {
  if (!isoDate) return '—'
  const [y, m, d] = isoDate.split('-').map(Number)
  return `${MONTHS[m - 1].slice(0, 3)} ${d}, ${y}`
}

const CHANNEL_LABEL: Record<string, string> = { airbnb: 'Airbnb', vrbo: 'Vrbo', direct: 'Direct' }

function problemsNeedingAck(row: RocRow) {
  return row.problems.filter(p => p.severity === 'review' && p.ack_key)
}

/** Status as it stands with THIS browser's acknowledgment notes, before the server re-checks. */
function localStatus(row: RocRow, acks: Record<string, string>): RocRow['status'] {
  if (row.problems.some(p => p.severity === 'error')) return 'error'
  if (problemsNeedingAck(row).some(p => !acks[p.ack_key!]?.trim())) return 'needs_review'
  return 'ready'
}

function StatusBadge({ row, acks }: { row: RocRow; acks: Record<string, string> }) {
  const status = localStatus(row, acks)
  if (status === 'ready') return <Badge variant="success">Ready</Badge>
  if (status === 'needs_review') {
    const first = problemsNeedingAck(row).find(p => !acks[p.ack_key!]?.trim())
    return (
      <Badge variant="warning" title={first?.message}>
        {first?.code === 'collected_mismatch'
          ? `Collected ${row.collected_difference > 0 ? '+' : '-'}$${money(Math.abs(row.collected_difference))}`
          : 'Needs review'}
      </Badge>
    )
  }
  const errors = row.problems.filter(p => p.severity === 'error')
  return (
    <Badge variant="destructive" title={errors.map(e => e.message).join('\n')}>
      {errors.length === 1 ? errors[0].message : `${errors.length} errors`}
    </Badge>
  )
}

function Amount({
  value,
  rateBp,
  showRate,
}: {
  value: number
  rateBp: number
  showRate?: boolean
}) {
  return (
    <td className={`px-2 py-2.5 text-right tabular-nums ${value === 0 ? 'text-textFaint' : ''}`}>
      {money(value)}
      {showRate && rateBp > 0 && (
        <span className="block text-[11px] text-textFaint">{formatRate(rateBp)}</span>
      )}
    </td>
  )
}

function ProblemList({
  row,
  acks,
  onAck,
}: {
  row: RocRow
  acks: Record<string, string>
  onAck: (key: string, note: string) => void
}) {
  if (row.problems.length === 0) return null
  const tone = (p: RocProblem) =>
    p.severity === 'error'
      ? 'text-danger'
      : p.severity === 'review'
        ? 'text-amber'
        : 'text-textMuted'
  return (
    <ul className="space-y-3">
      {row.problems.map((p, i) => (
        <li key={`${p.code}-${p.reservation_id ?? i}`} className="text-sm">
          <div className={tone(p)}>{p.message}</div>
          {p.severity === 'review' && p.ack_key && (
            <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
              <Label htmlFor={`ack-${p.ack_key}`} className="shrink-0 text-textMuted">
                Acknowledgment note
              </Label>
              <Input
                id={`ack-${p.ack_key}`}
                placeholder="Why this is accepted (saved with the version)"
                value={acks[p.ack_key] ?? ''}
                onChange={e => onAck(p.ack_key!, e.target.value)}
              />
            </div>
          )}
        </li>
      ))}
    </ul>
  )
}

function RowDetail({
  row,
  acks,
  onAck,
}: {
  row: RocRow
  acks: Record<string, string>
  onAck: (key: string, note: string) => void
}) {
  return (
    // Sticky and capped to the visible width: the row spans the whole 1100px
    // table, so without this the note field sits off screen to the right.
    <div className="sticky left-0 max-w-[calc(100vw-3rem)] space-y-4 bg-surface2/50 px-4 py-4 lg:max-w-[min(calc(100vw-20rem),64rem)]">
      <div className="text-sm text-textMuted">
        {row.street}, {row.city} {row.zip}
        {row.municipality_name && <> · {row.municipality_name}</>} · operator{' '}
        {row.operator_name ?? '—'}
      </div>
      <ProblemList row={row} acks={acks} onAck={onAck} />
      <div className="overflow-x-auto">
        <table className="w-full text-sm tabular-nums">
          <caption className="mb-2 text-left text-sm font-medium text-text">
            Included reservations ({row.reservations.length})
          </caption>
          <thead className="text-textMuted">
            <tr>
              <th className="px-2 py-1.5 text-left font-medium">Reservation</th>
              <th className="px-2 py-1.5 text-left font-medium">Channel</th>
              <th className="px-2 py-1.5 text-left font-medium">Stay</th>
              <th className="px-2 py-1.5 text-right font-medium">Accommodation</th>
              <th className="px-2 py-1.5 text-right font-medium">Cleaning</th>
              <th className="px-2 py-1.5 text-right font-medium">Discounts</th>
              <th className="px-2 py-1.5 text-right font-medium">Taxable rent</th>
              <th className="px-2 py-1.5 text-right font-medium">Tax collected</th>
            </tr>
          </thead>
          <tbody>
            {row.reservations.map(r => (
              <tr key={r.reservation_id} className="border-t border-border">
                <td className="px-2 py-1.5">
                  <a
                    className="font-mono text-xs text-mint hover:underline"
                    href={`/reservations/${r.reservation_id}`}
                  >
                    {r.reservation_id}
                  </a>
                </td>
                <td className="px-2 py-1.5">{CHANNEL_LABEL[r.channel] ?? r.channel}</td>
                <td className="whitespace-nowrap px-2 py-1.5">
                  {shortDate(r.check_in)} → {shortDate(r.check_out)}
                  {r.exempt && (
                    <span className="ml-1 text-textFaint">(exempt, over 90 nights)</span>
                  )}
                </td>
                <td className="px-2 py-1.5 text-right">{money(r.accommodation ?? 0)}</td>
                <td className="px-2 py-1.5 text-right">{money(r.cleaning_fee ?? 0)}</td>
                <td className="px-2 py-1.5 text-right">{money(r.discounts ?? 0)}</td>
                <td className="px-2 py-1.5 text-right">{money(r.taxable_rent ?? 0)}</td>
                <td className="px-2 py-1.5 text-right">{money(r.collected_tax ?? 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {row.excluded_reservations.length > 0 && (
        <details className="text-sm">
          <summary className="cursor-pointer text-textMuted">
            Excluded for this property ({row.excluded_reservations.length})
          </summary>
          <ul className="mt-2 space-y-1">
            {row.excluded_reservations.map(r => (
              <li key={r.reservation_id} className="text-textMuted">
                <span className="font-mono text-xs">{r.reservation_id}</span> ·{' '}
                {CHANNEL_LABEL[r.channel] ?? r.channel} · {shortDate(r.check_in)} →{' '}
                {shortDate(r.check_out)} · {r.exclusion_reason}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  )
}

function VersionsTable({ period }: { period: string }) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [confirming, setConfirming] = useState<string | null>(null)
  const [confirmation, setConfirmation] = useState('')
  const [emailsFor, setEmailsFor] = useState<string | null>(null)
  const versions = useQuery({
    queryKey: ['roc-versions', period],
    queryFn: () => api.listRocVersions(period),
  })
  const confirmations = useQuery({
    queryKey: ['ma-tax-confirmations', period],
    queryFn: () => api.listMaTaxConfirmations(period),
  })
  const unmatched = confirmations.data?.confirmations.filter(c => c.status === 'unmatched') ?? []

  const matchTo = async (number: string, version: RocVersion) => {
    try {
      await api.matchMaTaxConfirmation(number, version.id)
      await queryClient.invalidateQueries({ queryKey: ['roc-versions', period] })
      await queryClient.invalidateQueries({ queryKey: ['ma-tax-confirmations', period] })
      toast({ title: `Confirmation ${number} matched to v${version.version}` })
    } catch (err: unknown) {
      toast({
        title: 'Could not match the confirmation',
        description: err instanceof Error ? err.message : undefined,
        variant: 'destructive',
      })
    }
  }

  const markSubmitted = async (version: RocVersion) => {
    try {
      await api.markRocVersionSubmitted(version.id, confirmation.trim() || undefined)
      await queryClient.invalidateQueries({ queryKey: ['roc-versions', period] })
      await queryClient.invalidateQueries({ queryKey: ['ma-tax-confirmations', period] })
      setConfirming(null)
      setConfirmation('')
      toast({ title: `v${version.version} marked as submitted` })
    } catch (err: unknown) {
      toast({
        title: 'Could not mark as submitted',
        description: err instanceof Error ? err.message : undefined,
        variant: 'destructive',
      })
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Generated files for {periodLabel(period)}</CardTitle>
        <CardDescription>
          Every generated file is kept with the calculation it was built from. Regenerating adds a
          new version; nothing is overwritten.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {unmatched.map(c => (
          <div
            key={c.confirmation_number}
            role="status"
            className="space-y-2 rounded-card border border-amber/30 bg-warningBg px-4 py-3 text-sm"
          >
            <div>
              MassTaxConnect confirmed <span className="font-mono">{c.confirmation_number}</span>{' '}
              (submitted {c.submitted_at_raw}), but it could not be matched to a single submitted
              version. Which file did you upload?
            </div>
            <div className="flex flex-wrap gap-2">
              {(versions.data?.versions ?? [])
                .filter(v => !v.confirmation_number)
                .map(v => (
                  <Button
                    key={v.id}
                    size="sm"
                    variant="outline"
                    onClick={() => matchTo(c.confirmation_number, v)}
                  >
                    It was v{v.version}
                  </Button>
                ))}
            </div>
          </div>
        ))}
        {versions.isLoading ? (
          <LoadingSkeleton className="h-20 w-full" />
        ) : versions.isError ? (
          <ErrorState error={versions.error} onRetry={() => versions.refetch()} />
        ) : versions.data?.versions.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm tabular-nums">
              <thead className="text-textMuted">
                <tr>
                  <th className="px-2 py-2 text-left font-medium">Version</th>
                  <th className="px-2 py-2 text-left font-medium">Generated</th>
                  <th className="px-2 py-2 text-right font-medium">Properties</th>
                  <th className="px-2 py-2 text-right font-medium">Stays</th>
                  <th className="px-2 py-2 text-right font-medium">Taxable rents</th>
                  <th className="px-2 py-2 text-right font-medium">Total tax</th>
                  <th className="px-2 py-2 text-left font-medium">Status</th>
                  <th className="px-2 py-2 text-left font-medium">
                    <span className="sr-only">Files</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {versions.data.versions.map(v => (
                  <Fragment key={v.id}>
                    <tr className="border-t border-border align-top">
                      <td className="px-2 py-2.5">
                        v{v.version} {v.is_latest && <Badge variant="success">Latest</Badge>}{' '}
                        {v.is_amendment && <Badge variant="warning">Amended</Badge>}
                      </td>
                      <td className="px-2 py-2.5">
                        {formatDateTime(v.created_at)}
                        <span className="block text-xs text-textFaint">
                          {v.created_by ?? 'service token'}
                        </span>
                      </td>
                      <td className="px-2 py-2.5 text-right">{v.row_count}</td>
                      <td className="px-2 py-2.5 text-right">{v.reservation_count}</td>
                      <td className="px-2 py-2.5 text-right">
                        {money(v.taxable_rents_cents / 100)}
                      </td>
                      <td className="px-2 py-2.5 text-right">{money(v.total_tax_cents / 100)}</td>
                      <td className="px-2 py-2.5">
                        {v.submitted_at ? (
                          <span>
                            Submitted {formatDateTime(v.submitted_at)}
                            {v.confirmation_number && (
                              <span className="block font-mono text-xs text-textMuted">
                                {v.confirmation_number}
                              </span>
                            )}
                          </span>
                        ) : confirming === v.id ? (
                          <div className="flex flex-col gap-2">
                            <Label htmlFor={`conf-${v.id}`} className="text-xs text-textMuted">
                              MassTaxConnect confirmation (optional)
                            </Label>
                            <Input
                              id={`conf-${v.id}`}
                              className="font-mono"
                              placeholder="1-762-095-712"
                              value={confirmation}
                              onChange={e => setConfirmation(e.target.value)}
                            />
                            <div className="flex gap-2">
                              <Button size="sm" onClick={() => markSubmitted(v)}>
                                Confirm submitted
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => setConfirming(null)}>
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => setConfirming(v.id)}>
                            Mark as submitted
                          </Button>
                        )}
                      </td>
                      <td className="px-2 py-2.5">
                        <div className="flex flex-wrap gap-2">
                          <Button asChild size="sm" variant="outline">
                            <a href={api.rocVersionFileUrl(v.id, 'xlsx')}>
                              <Download className="mr-1 h-4 w-4" aria-hidden="true" />
                              XLSX
                            </a>
                          </Button>
                          {v.confirmation_number && (
                            <Button
                              size="sm"
                              variant="outline"
                              aria-expanded={emailsFor === v.id}
                              onClick={() => setEmailsFor(emailsFor === v.id ? null : v.id)}
                            >
                              Owner emails
                            </Button>
                          )}
                          <Button asChild size="sm" variant="ghost">
                            <a href={api.rocVersionFileUrl(v.id, 'calculation')}>
                              <FileJson className="mr-1 h-4 w-4" aria-hidden="true" />
                              Calculation
                            </a>
                          </Button>
                        </div>
                      </td>
                    </tr>
                    {emailsFor === v.id && (
                      <tr className="border-t border-border">
                        <td colSpan={8} className="px-2 py-4">
                          <OwnerEmailsPanel versionId={v.id} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-textMuted">
            No files generated for {periodLabel(period)} yet.
          </p>
        )}
      </CardContent>
    </Card>
  )
}

/** First load of a month: the page's shape, and what it is waiting for. */
function PreviewLoading({ period }: { period: string }) {
  return (
    <div className="space-y-6" aria-busy="true">
      <div role="status" className="flex items-center gap-2 text-sm text-textMuted">
        <Spinner />
        Calculating {periodLabel(period)}: reading every property&apos;s reservations...
      </div>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map(i => (
          <LoadingSkeleton key={i} className="h-24 w-full" />
        ))}
      </div>
      <Card>
        <CardContent className="space-y-3 pt-6">
          {Array.from({ length: 8 }, (_, i) => (
            <LoadingSkeleton key={i} className="h-8 w-full" />
          ))}
        </CardContent>
      </Card>
    </div>
  )
}

/**
 * Massachusetts Form ROC (CAP-548): pick a month, check every property's row,
 * acknowledge what needs acknowledging, then generate the MassTaxConnect file.
 * The server recomputes everything on generate; this page never sends numbers.
 */
export function TaxReportsPage() {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const initial = defaultPeriod()
  const [year, setYear] = useState(initial.year)
  const [month, setMonth] = useState(initial.month)
  const [unchecked, setUnchecked] = useState<Set<string>>(new Set())
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [acks, setAcks] = useState<Record<string, string>>({})
  const [generating, setGenerating] = useState(false)
  const period = `${year}-${String(month).padStart(2, '0')}`

  const preview = useQuery({
    queryKey: ['roc-preview', period],
    queryFn: () => api.getRocPreview(period),
    // Keep the previous month on screen (dimmed) while the next one is
    // calculated, instead of dropping the whole page to a blank block.
    placeholderData: keepPreviousData,
  })
  // True while the table still shows the PREVIOUS month's numbers.
  const isSwitching = preview.isPlaceholderData

  const rows = preview.data?.rows ?? []
  const selected = rows.filter(r => !unchecked.has(r.property_id))
  const periodBlocked = preview.data?.blocking_reasons.find(r => r.includes('has not ended'))
  const blockers = selected.filter(r => localStatus(r, acks) !== 'ready')
  const canGenerate =
    !!preview.data && !isSwitching && !periodBlocked && selected.length > 0 && blockers.length === 0

  const totals = (() => {
    const sum = (pick: (r: RocRow) => number) =>
      selected.reduce((total, r) => total + Math.round(pick(r) * 100), 0) / 100
    return {
      reservations: selected.reduce((n, r) => n + r.reservation_ids.length, 0),
      total_rents: sum(r => r.total_rents),
      taxable_rents: sum(r => r.taxable_rents),
      state_tax: sum(r => r.state_tax),
      local_option_tax: sum(r => r.local_option_tax),
      convention_center_fund: sum(r => r.convention_center_fund),
      water_protection_fund: sum(r => r.water_protection_fund),
      community_impact_due: sum(r => r.community_impact_due),
      tourism_fee: sum(r => r.tourism_fee),
      total_tax: sum(r => r.total_tax),
      collected_tax: sum(r => r.collected_tax),
    }
  })()

  const toggle = (set: Set<string>, id: string) => {
    const next = new Set(set)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  }

  const onPeriodChange = (nextYear: number, nextMonth: number) => {
    setYear(nextYear)
    setMonth(nextMonth)
    setUnchecked(new Set())
    setExpanded(new Set())
    setAcks({})
  }

  const onGenerate = async () => {
    setGenerating(true)
    try {
      const acknowledgments = Object.fromEntries(
        Object.entries(acks).filter(([, note]) => note.trim())
      )
      const result = await api.createRocVersion({
        period,
        excluded_property_ids: [...unchecked],
        acknowledgments,
      })
      await queryClient.invalidateQueries({ queryKey: ['roc-versions', period] })
      toast({
        title: `Generated v${result.version.version} for ${periodLabel(period)}`,
        description: `${result.version.row_count} properties, $${money(result.version.total_tax_cents / 100)} tax. Downloading.`,
      })
      window.location.assign(api.rocVersionFileUrl(result.version.id, 'xlsx'))
    } catch (err: unknown) {
      await preview.refetch()
      toast({
        title: 'The file was not generated',
        description:
          err instanceof Error
            ? err.message
            : 'The month changed since the preview; review it again.',
        variant: 'destructive',
      })
    } finally {
      setGenerating(false)
    }
  }

  const thisYear = new Date().getFullYear()
  const yearOptions = [thisYear, thisYear - 1, thisYear - 2]
  const excludedCounts = preview.data?.excluded_counts ?? {}

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="flex flex-wrap items-end gap-4 pt-6">
          <div className="space-y-1.5">
            <Label htmlFor="roc-month">Tax month</Label>
            <Select value={String(month)} onValueChange={v => onPeriodChange(year, Number(v))}>
              <SelectTrigger id="roc-month" className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTHS.map((name, i) => (
                  <SelectItem key={name} value={String(i + 1)}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="roc-year">Tax year</Label>
            <Select value={String(year)} onValueChange={v => onPeriodChange(Number(v), month)}>
              <SelectTrigger id="roc-year" className="w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {yearOptions.map(y => (
                  <SelectItem key={y} value={String(y)}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="max-w-md pb-2 text-xs text-textFaint">
            Stays are reported in the month they check out. Airbnb stays are left out because Airbnb
            remits the tax.
          </p>
          <div className="ml-auto flex flex-col items-end gap-1.5">
            <Button onClick={onGenerate} disabled={!canGenerate || generating}>
              <Download className="mr-2 h-4 w-4" aria-hidden="true" />
              {generating ? 'Generating...' : 'Generate MA ROC file'}
            </Button>
            {preview.isFetching && !preview.isLoading && (
              <span role="status" className="flex items-center gap-2 text-xs text-textMuted">
                <Spinner />
                Calculating {periodLabel(period)}...
              </span>
            )}
            {preview.data && !preview.isFetching && !canGenerate && (
              <span className="text-xs text-amber">
                {periodBlocked
                  ? `${periodLabel(period)} has not ended yet`
                  : selected.length === 0
                    ? 'No properties selected'
                    : `Resolve ${blockers.length} ${blockers.length === 1 ? 'property' : 'properties'} to generate`}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {preview.isLoading ? (
        <PreviewLoading period={period} />
      ) : preview.isError ? (
        <ErrorState error={preview.error} onRetry={() => preview.refetch()} />
      ) : preview.data ? (
        <div
          aria-busy={isSwitching}
          className={`space-y-6 transition-opacity ${isSwitching ? 'pointer-events-none opacity-40' : ''}`}
        >
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Card>
              <CardContent className="pt-5">
                <div className="text-xs text-textMuted">Properties in the file</div>
                <div className="mt-1 text-2xl font-semibold tabular-nums">{selected.length}</div>
                <div className="text-xs text-textFaint">
                  {selected.length} of {rows.length} selected
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-5">
                <div className="text-xs text-textMuted">Stays included</div>
                <div className="mt-1 text-2xl font-semibold tabular-nums">
                  {totals.reservations}
                </div>
                <div className="text-xs text-textFaint">Capehost collected the tax on these</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-5">
                <div className="text-xs text-textMuted">Stays left out</div>
                <div className="mt-1 text-2xl font-semibold tabular-nums">
                  {Object.values(excludedCounts).reduce((a, b) => a + b, 0)}
                </div>
                <div className="text-xs text-textFaint">
                  {excludedCounts.airbnb_remits ?? 0} Airbnb (Airbnb remits),{' '}
                  {excludedCounts.cancelled ?? 0} cancelled
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-5">
                <div className="text-xs text-textMuted">Tax due</div>
                <div className="mt-1 text-2xl font-semibold tabular-nums">
                  ${money(totals.total_tax)}
                </div>
                <div
                  className={`text-xs ${Math.abs(totals.collected_tax - totals.total_tax) >= 0.01 * Math.max(1, totals.reservations) ? 'text-amber' : 'text-textFaint'}`}
                >
                  Collected ${money(totals.collected_tax)}
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardContent className="overflow-x-auto p-0">
              {rows.length === 0 ? (
                <p className="p-6 text-sm text-textMuted">
                  Nothing to report for {periodLabel(period)}: no Capehost-collected stays checked
                  out in Massachusetts that month.
                </p>
              ) : (
                <table className="w-full min-w-[1100px] text-[13px] tabular-nums">
                  <thead className="text-textMuted">
                    <tr className="border-b border-border">
                      <th className="w-10 px-2 py-2.5">
                        <input
                          type="checkbox"
                          aria-label="Include every property"
                          checked={unchecked.size === 0}
                          onChange={() =>
                            setUnchecked(
                              unchecked.size === 0
                                ? new Set(rows.map(r => r.property_id))
                                : new Set()
                            )
                          }
                        />
                      </th>
                      <th className="px-2 py-2.5 text-left font-medium">Property</th>
                      <th className="px-2 py-2.5 text-left font-medium">Certificate ID</th>
                      <th className="px-2 py-2.5 text-right font-medium">Stays</th>
                      <th className="px-2 py-2.5 text-right font-medium">Total rents</th>
                      <th className="px-2 py-2.5 text-right font-medium">Taxable rents</th>
                      <th className="px-2 py-2.5 text-right font-medium">State tax</th>
                      <th className="px-2 py-2.5 text-right font-medium">Local option</th>
                      <th className="px-2 py-2.5 text-right font-medium">Conv. center</th>
                      <th className="px-2 py-2.5 text-right font-medium">Water fund</th>
                      <th className="px-2 py-2.5 text-right font-medium">Community impact</th>
                      <th className="px-2 py-2.5 text-right font-medium">Tourism</th>
                      <th className="px-2 py-2.5 text-right font-medium">Total tax</th>
                      <th className="px-2 py-2.5 text-right font-medium">Collected</th>
                      <th className="px-2 py-2.5 text-left font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(row => {
                      const isSelected = !unchecked.has(row.property_id)
                      const isOpen = expanded.has(row.property_id)
                      return (
                        <Fragment key={row.property_id}>
                          <tr
                            className={`border-b border-border ${isSelected ? '' : 'opacity-50'}`}
                          >
                            <td className="px-2 py-2.5 text-center">
                              <input
                                type="checkbox"
                                aria-label={`Include ${row.property_name}`}
                                checked={isSelected}
                                onChange={() => setUnchecked(toggle(unchecked, row.property_id))}
                              />
                            </td>
                            <td className="px-2 py-2.5 text-left">
                              <button
                                type="button"
                                className="flex items-center gap-1 whitespace-nowrap text-left text-text hover:text-mint focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint"
                                aria-expanded={isOpen}
                                onClick={() => setExpanded(toggle(expanded, row.property_id))}
                              >
                                {isOpen ? (
                                  <ChevronDown className="h-4 w-4" aria-hidden="true" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" aria-hidden="true" />
                                )}
                                {row.property_name}
                              </button>
                            </td>
                            <td className="px-2 py-2.5 text-left font-mono text-xs">
                              {row.certificate_id ?? '—'}
                            </td>
                            <td className="px-2 py-2.5 text-right">{row.reservation_ids.length}</td>
                            <td className="px-2 py-2.5 text-right">{money(row.total_rents)}</td>
                            <td className="px-2 py-2.5 text-right">{money(row.taxable_rents)}</td>
                            <Amount value={row.state_tax} rateBp={row.state_tax_rate_bp} showRate />
                            <Amount
                              value={row.local_option_tax}
                              rateBp={row.local_tax_rate_bp}
                              showRate
                            />
                            <Amount
                              value={row.convention_center_fund}
                              rateBp={row.convention_center_rate_bp}
                              showRate
                            />
                            <Amount
                              value={row.water_protection_fund}
                              rateBp={row.water_protection_rate_bp}
                              showRate
                            />
                            <Amount
                              value={row.community_impact_due}
                              rateBp={row.community_impact_rate_bp}
                              showRate
                            />
                            <Amount
                              value={row.tourism_fee}
                              rateBp={row.tourism_fee_rate_bp}
                              showRate
                            />
                            <td className="px-2 py-2.5 text-right font-medium">
                              {money(row.total_tax)}
                            </td>
                            <td
                              className={`px-2 py-2.5 text-right ${row.problems.some(p => p.code === 'collected_mismatch') ? 'text-amber' : ''}`}
                            >
                              {money(row.collected_tax)}
                            </td>
                            <td className="max-w-[220px] px-2 py-2.5 text-left">
                              <StatusBadge row={row} acks={acks} />
                            </td>
                          </tr>
                          {isOpen && (
                            <tr className="border-b border-border">
                              <td colSpan={15} className="p-0">
                                <RowDetail
                                  row={row}
                                  acks={acks}
                                  onAck={(key, note) => setAcks(prev => ({ ...prev, [key]: note }))}
                                />
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      )
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="font-semibold">
                      <td />
                      <td className="px-2 py-3 text-left">Total (selected)</td>
                      <td />
                      <td className="px-2 py-3 text-right">{totals.reservations}</td>
                      <td className="px-2 py-3 text-right">{money(totals.total_rents)}</td>
                      <td className="px-2 py-3 text-right">{money(totals.taxable_rents)}</td>
                      <td className="px-2 py-3 text-right">{money(totals.state_tax)}</td>
                      <td className="px-2 py-3 text-right">{money(totals.local_option_tax)}</td>
                      <td className="px-2 py-3 text-right">
                        {money(totals.convention_center_fund)}
                      </td>
                      <td className="px-2 py-3 text-right">
                        {money(totals.water_protection_fund)}
                      </td>
                      <td className="px-2 py-3 text-right">{money(totals.community_impact_due)}</td>
                      <td className="px-2 py-3 text-right">{money(totals.tourism_fee)}</td>
                      <td className="px-2 py-3 text-right">{money(totals.total_tax)}</td>
                      <td className="px-2 py-3 text-right">{money(totals.collected_tax)}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              )}
            </CardContent>
          </Card>

          {blockers.length > 0 && (
            <div
              role="status"
              className="space-y-1 rounded-card border border-amber/30 bg-warningBg px-5 py-4 text-sm"
            >
              <div className="font-medium text-amber">
                {blockers.length} {blockers.length === 1 ? 'property needs' : 'properties need'}{' '}
                attention before this file can be generated
              </div>
              {blockers.map(r => (
                <div key={r.property_id}>
                  <span className="font-medium">{r.property_name}:</span>{' '}
                  {localStatus(r, acks) === 'error'
                    ? r.problems
                        .filter(p => p.severity === 'error')
                        .map(p => p.message)
                        .join('; ')
                    : 'expand the row and add an acknowledgment note'}
                </div>
              ))}
            </div>
          )}

          {preview.data.properties_without_stays.length > 0 && (
            <p className="text-xs text-textFaint">
              Reported with zeros (no Capehost-collected stays this month):{' '}
              {preview.data.properties_without_stays.map(p => p.name).join(', ')}.
            </p>
          )}
        </div>
      ) : null}

      <VersionsTable period={period} />
    </div>
  )
}
